import { create } from 'zustand';

/**
 * The Piper assets: ~90 MB, fetched on demand, kept in the Cache API.
 *
 * ── Why they are not in the app bundle ──
 *
 * 2.8 says to serve them separately, and the numbers say why: 90 MB in the precache would be downloaded by every
 * install of a feature that ships **off**, and it would turn a deploy from seconds into minutes. They live in a
 * public blob container, uploaded once by `scripts/provision-assets.sh` and then essentially immutable — the
 * model has a version in its name, so a new voice is a new URL and this cache can never go stale.
 *
 * ── Why the Cache API and not OPFS ──
 *
 * Phase 0's probe used OPFS because it was the shortest path to a measurement. The project rule is binaries in
 * the Cache API (INV-04), and it is the right store here for the same reason it is right for audio:
 * it holds opaque responses, it is what the service worker already speaks, and `caches.delete` frees the space
 * immediately — which Phase 0 confirmed on this device.
 *
 * The awkward part is the phonemiser's 18 MB `.data` file. Emscripten insists on fetching its preload from a
 * **URL**, so cached bytes are handed back as a `blob:` URL. That is the piece that makes "binaries in the Cache
 * API" work with a wasm module that wants to do its own fetching.
 */

const CACHE_NAME = 'sags-piper-v1';

/**
 * The cache key for an asset — absolute, and that is the whole point.
 *
 * `cache.match('foo.wasm')` resolves the string against the **calling scope's** base URL. The download runs on the
 * main thread, where that is the document; `assetBytes`/`assetUrl` are also called from inside the worker, whose
 * base is its own script (`/assets/piperWorker-*.js` in a build). Relative keys are therefore written under one
 * URL and read under another, and the worker's lookups miss every time — a finished 90 MB download that reports
 * itself ready and then cannot synthesise a word.
 */
const cacheKey = (name: string): string => new URL(`/piper/${name}`, self.location.origin).href;

/**
 * Where the assets are served from.
 *
 * A separate public storage account, not the one holding the encrypted backups: that account sets
 * `allowBlobPublicAccess false` deliberately, and a public container there would have meant weakening it. Public
 * model weights and private backups have no business sharing an account.
 *
 * The account is `stsofsagdevassets` rather than `stsagsassets` because hosting moved subscriptions (ADR-048)
 * and a globally unique storage name is not released by the old subscription going quiet — the old name still
 * resolves, and answers `AccountIsDisabled`. **This must equal `ACCOUNT_NAME` in
 * `scripts/provision-assets.sh`**, which is the one place the container is actually created; if they disagree
 * the client reports that the download did not work and can say nothing better than that.
 */
const DEFAULT_BASE = 'https://stsofsagdevassets.blob.core.windows.net/piper';

export const assetBase = (): string =>
  (import.meta.env.VITE_PIPER_BASE as string | undefined)?.replace(/\/$/u, '') ?? DEFAULT_BASE;

/** The voice, decided with the family: male, to match the Azure default (`de-CH-JanNeural`). */
export const VOICE_ID = 'de_DE-thorsten-medium';

export interface PiperAsset {
  /** Path under the asset base, and the cache key. */
  name: string;
  /**
   * Expected size in bytes, from Phase 0's own measurements.
   *
   * Present so a **truncated download is a failure rather than a corrupt model**. Without it, a connection that
   * drops at 90 % produces an `.onnx` that ORT rejects with an opaque error at the moment somebody first tries to
   * speak offline — which is the worst possible time to discover it.
   */
  bytes: number;
}

/**
 * Everything needed to synthesise offline, and nothing else.
 *
 * The ORT JavaScript is **not** here: it comes from `onnxruntime-web` on npm and is bundled with the app. Phase 0
 * was specific about which dependency was the problem — "the heavy lifting is ONNX Runtime and espeak-ng, both of
 * which are maintained" — so what gets vendored is the stale Piper wrapper, not the engine.
 */
export const PIPER_ASSETS: readonly PiperAsset[] = [
  // The ONNX Runtime WASM binary, from the `onnxruntime-web` package rather than a CDN — so it works offline,
  // and so no third-party host learns when this person speaks. The filename says "threaded" and it runs
  // single-threaded: that is the only SIMD build ORT 1.20 ships, and Phase 0 measured 1 thread and 4 threads at
  // an identical 463 ms, so nothing is lost. (Phase 0's probe used an older 10.6 MB `ort-wasm-simd.wasm`.)
  { name: 'ort-wasm-simd-threaded.wasm', bytes: 11_246_032 },
  // espeak-ng, compiled. The `.data` is its dictionary set.
  { name: 'piper_phonemize.wasm', bytes: 635_212 },
  { name: 'piper_phonemize.data', bytes: 18_077_249 },
  // The voice.
  { name: `${VOICE_ID}.onnx`, bytes: 63_201_294 },
  { name: `${VOICE_ID}.onnx.json`, bytes: 0 },
];

/** Sum of the known sizes, for a progress bar that means something before the first byte arrives. */
export const TOTAL_BYTES = PIPER_ASSETS.reduce((sum, asset) => sum + asset.bytes, 0);

export interface DownloadProgress {
  receivedBytes: number;
  totalBytes: number;
  /** Which asset is in flight, so a stall is legible rather than mysterious. */
  current: string | null;
}

/**
 * Why a download failed, in the only two flavours that lead anywhere different.
 *
 * `network` is the connection: the request never got an answer, and retrying on better Wi-Fi is the fix. `server`
 * is an answer that was wrong — a 404 because the assets were never uploaded, or a size that says the file on the
 * other end is not the one this build expects. Telling a caregiver to check the Wi-Fi in that second case sends
 * them to reboot a router that is working perfectly, which is how this bug wasted an evening.
 */
export interface DownloadError {
  kind: 'network' | 'server';
  /** The technical message. Not a caregiver's sentence — it is the line they can pass on to someone who can act. */
  message: string;
}

interface AssetState {
  downloaded: boolean | null;
  progress: DownloadProgress | null;
  error: DownloadError | null;
  sizeBytes: number | null;
  check: () => Promise<void>;
  download: () => Promise<boolean>;
  remove: () => Promise<void>;
}

export const usePiperAssets = create<AssetState>((set) => ({
  downloaded: null,
  progress: null,
  error: null,
  sizeBytes: null,

  check: async () => {
    const present = await isDownloaded();
    set({ downloaded: present, sizeBytes: present ? await cachedSize() : 0 });
  },

  download: async () => {
    set({ error: null, progress: { receivedBytes: 0, totalBytes: TOTAL_BYTES, current: null } });
    try {
      await downloadAll((progress) => set({ progress }));
      set({ downloaded: true, progress: null, sizeBytes: await cachedSize() });
      return true;
    } catch (err) {
      // Logged as well as stored. This was the only piper failure path that logged nothing, which meant a device
      // reporting "Laden hat nicht geklappt" gave up its reason to nobody — not the screen, not the console.
      console.error('[piper] download failed', err);
      // A half-downloaded cache is left in place on purpose: the next attempt skips whatever already arrived,
      // which matters on a connection that drops.
      set({ error: classify(err), progress: null });
      return false;
    }
  },

  remove: async () => {
    await caches.delete(CACHE_NAME);
    set({ downloaded: false, sizeBytes: 0, progress: null });
  },
}));

/**
 * Thrown when the other end answered and the answer was wrong — as opposed to a `fetch` that never got one.
 *
 * A marker class rather than a parsed message: `fetch` rejects with a plain `TypeError` whose text differs between
 * engines ("Load failed" in WebKit, "NetworkError…" elsewhere), so the reliable signal is which errors *we* raise.
 */
class ServerError extends Error {}

const classify = (err: unknown): DownloadError => ({
  kind: err instanceof ServerError ? 'server' : 'network',
  message: err instanceof Error ? err.message : String(err),
});

export async function isDownloaded(): Promise<boolean> {
  if (typeof caches === 'undefined') return false;
  const cache = await caches.open(CACHE_NAME);
  for (const asset of PIPER_ASSETS) {
    const hit = await cache.match(cacheKey(asset.name));
    if (!hit) return false;
  }
  return true;
}

async function cachedSize(): Promise<number> {
  const cache = await caches.open(CACHE_NAME);
  let total = 0;
  for (const asset of PIPER_ASSETS) {
    const hit = await cache.match(cacheKey(asset.name));
    if (hit) total += (await hit.clone().arrayBuffer()).byteLength;
  }
  return total;
}

/**
 * Fetch whatever is missing, reporting progress as it goes.
 *
 * Sequential, not parallel. Five concurrent streams on a household connection would make the progress bar jump
 * about and would not finish sooner — and the one thing a caregiver needs from a 90 MB download is to be able to
 * tell whether it is still moving.
 */
async function downloadAll(onProgress: (progress: DownloadProgress) => void): Promise<void> {
  const cache = await caches.open(CACHE_NAME);
  let received = 0;

  for (const asset of PIPER_ASSETS) {
    const existing = await cache.match(cacheKey(asset.name));
    if (existing) {
      received += (await existing.clone().arrayBuffer()).byteLength;
      onProgress({ receivedBytes: received, totalBytes: TOTAL_BYTES, current: null });
      continue;
    }

    onProgress({ receivedBytes: received, totalBytes: TOTAL_BYTES, current: asset.name });

    const response = await fetch(`${assetBase()}/${asset.name}`, { cache: 'no-store' });
    if (!response.ok || !response.body) {
      // A 404 here means the assets were never uploaded — see scripts/provision-assets.sh, whose verification step
      // exists so that this is caught at provisioning time rather than by a caregiver holding an iPad.
      throw new ServerError(`piper: ${asset.name} failed with ${response.status}`);
    }

    const chunks: Uint8Array[] = [];
    const reader = response.body.getReader();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.byteLength;
      onProgress({ receivedBytes: received, totalBytes: TOTAL_BYTES, current: asset.name });
    }

    const bytes = concat(chunks);
    // A truncated download must not be cached. `bytes: 0` means "size unknown", which is only the small config.
    if (asset.bytes > 0 && bytes.byteLength !== asset.bytes) {
      throw new ServerError(
        `piper: ${asset.name} is ${bytes.byteLength} bytes, expected ${asset.bytes}`,
      );
    }

    await cache.put(cacheKey(asset.name), new Response(bytes, { headers: contentType(asset.name) }));
  }
}

function concat(chunks: readonly Uint8Array[]): Uint8Array<ArrayBuffer> {
  const total = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return out;
}

const contentType = (name: string): Record<string, string> => ({
  'Content-Type': name.endsWith('.json')
    ? 'application/json'
    : name.endsWith('.wasm')
      ? 'application/wasm'
      : 'application/octet-stream',
});

/** Cached bytes, or null when this asset has not been downloaded. */
export async function assetBytes(name: string): Promise<ArrayBuffer | null> {
  if (typeof caches === 'undefined') return null;
  const cache = await caches.open(CACHE_NAME);
  const hit = await cache.match(cacheKey(name));
  return hit ? hit.arrayBuffer() : null;
}

/**
 * A `blob:` URL for a cached asset.
 *
 * Only for the two files that insist on fetching themselves: ORT locates its `.wasm` by URL, and Emscripten
 * locates the phonemiser's `.data` preload by URL. Everything else is handed bytes directly.
 */
export async function assetUrl(name: string): Promise<string | null> {
  const bytes = await assetBytes(name);
  if (!bytes) return null;
  return URL.createObjectURL(new Blob([bytes], contentType(name)));
}
